import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type TestimonialItem = {
  id: number;
  name: string;
  designation: string;
  quote: string;
  image: string;
  seoTitle: string;
  seoKeywords: string;
};

const Testimonials = () => {
  const initialData: TestimonialItem[] = [
    {
      id: 1,
      name: "Dr. Alok Sharma",
      designation: "Govt. Medical Officer",
      quote:
        "Sevaa's rapid response during the recent health crisis was nothing short of miraculous. Their dedication saved countless lives.",
      image:
        "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200&h=200&auto=format&fit=crop",
      seoTitle:
        "Healthcare support testimonial for Sevaa Foundation NGO medical camps",
      seoKeywords:
        "Dedicated to improving rural healthcare through free medical camps and blood donation drives across India.",
    },
    {
      id: 2,
      name: "Sunita Reddy",
      designation: "Sevaa Volunteer",
      quote:
        "Working on the ground with Sevaa transformed my understanding of poverty. Their focus on empowerment makes them different.",
      image:
        "https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=200&h=200&auto=format&fit=crop",
      seoTitle:
        "Volunteer testimonial for Sevaa Foundation charity work",
      seoKeywords:
        "Join our network of dedicated NGO volunteers working tirelessly for social welfare and community upliftment.",
    },
    {
      id: 3,
      name: "Rahul Mehra",
      designation: "TechforGood India",
      quote:
        "Their program is a masterclass in modern livelihood training. A must-watch for anyone who doubts grassroots work.",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&auto=format&fit=crop",
      seoTitle:
        "Livelihood and skill development testimonial",
      seoKeywords:
        "Empowering youth and women through skill development, livelihood training, and sustainable self-employment programs.",
    },
    {
      id: 4,
      name: "Priya Patel",
      designation: "Rural Educator",
      quote:
        "The education kits provided to our village school brought smiles to hundreds of children. Sevaa is truly building the future.",
      image:
        "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&auto=format&fit=crop",
      seoTitle:
        "Child education testimonial for school kit donation",
      seoKeywords:
        "Supporting child education by donating school kits and ensuring every underprivileged child gets the chance to study.",
    },
    {
      id: 5,
      name: "Amit Singh",
      designation: "Community Leader",
      quote:
        "During the winter relief drive, Sevaa reached the most remote parts of our district when no one else did. Absolute lifesavers.",
      image:
        "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&h=200&auto=format&fit=crop",
      seoTitle:
        "Relief work testimonial for hunger support and charity drives",
      seoKeywords:
        "Providing disaster relief, winter blanket donations, and essential hunger support to families in extreme need.",
    },
  ];

  const [cards, setCards] = useState<TestimonialItem[]>(initialData);

  const handleNext = () => {
    setCards((prevCards) => {
      const newArray = [...prevCards];
      const firstItem = newArray.shift();

      if (firstItem) {
        newArray.push(firstItem);
      }

      return newArray;
    });
  };

  const handlePrev = () => {
    setCards((prevCards) => {
      const newArray = [...prevCards];
      const lastItem = newArray.pop();

      if (lastItem) {
        newArray.unshift(lastItem);
      }

      return newArray;
    });
  };

  return (
    <section
      id="testimonials"
      aria-labelledby="testimonials-heading"
      className="py-12 md:py-16 bg-slate-50 font-poppins overflow-hidden relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12 relative">
          <motion.span
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-orange-600 font-extrabold text-[11px] uppercase tracking-[0.2em] mb-4 block"
          >
            Voices of Trust
          </motion.span>

          <h2
            id="testimonials-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight"
          >
            Our Impact Through <br /> Their Eyes.
          </h2>

          <p className="mt-5 max-w-3xl mx-auto text-slate-600 text-sm md:text-base font-medium leading-relaxed">
            Real voices from people connected with Sevaa Foundation&apos;s NGO
            work in child education, healthcare support, hunger relief, women
            empowerment, volunteer programs and social welfare initiatives
            across India.
          </p>
        </div>

        {/* 3D Stacked Layout Area */}
        <div className="relative flex justify-center items-center h-[500px] w-full max-w-7xl mx-auto">
          <button
            onClick={handlePrev}
            className="absolute -left-4 md:left-0 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-xl hover:bg-orange-50 hover:scale-110 transition-all text-orange-600 border border-slate-100"
            aria-label="Show previous Sevaa Foundation testimonial"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 w-full px-8 md:px-12 items-center">
            <AnimatePresence mode="popLayout">
              {cards.slice(0, 3).map((item, index) => {
                const isCenter = index === 1;

                return (
                  <motion.article
                    layout
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                      transition: { duration: 0.2 },
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 20,
                    }}
                    className={`relative w-full rounded-2xl shadow-2xl transition-colors duration-300 flex flex-col items-center text-center ${
                      isCenter
                        ? "bg-[#0f766e] text-white p-12 z-20 scale-110 md:scale-110 lg:scale-125 shadow-[#0f766e]/30"
                        : "bg-white text-slate-800 p-8 scale-95 opacity-50 hover:opacity-100 z-10 hidden md:flex"
                    }`}
                    aria-label={item.seoTitle}
                  >
                    {isCenter && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute -top-4 right-8 bg-orange-500 w-12 h-16 rounded-b-lg flex items-start justify-center pt-2 shadow-lg"
                        aria-hidden="true"
                      >
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                      </motion.div>
                    )}

                    <div
                      className={`rounded-xl overflow-hidden shadow-lg mb-6 transition-all duration-300 ${
                        isCenter ? "w-20 h-20 md:w-24 md:h-24 border-4 border-white/20" : "w-16 h-16 md:w-20 md:h-20"
                      }`}
                    >
                      <img
                        src={item.image}
                        alt={`${item.name}, ${item.designation} testimonial for Sevaa Foundation NGO`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    <h3 className={`text-lg md:text-xl font-black mb-1 ${isCenter ? "text-white" : "text-slate-900"}`}>
                      {item.name}
                    </h3>

                    <p className={`text-[9px] md:text-[10px] font-bold uppercase tracking-widest mb-4 md:mb-6 ${isCenter ? "text-teal-200" : "text-slate-500"}`}>
                      {item.designation}
                    </p>

                    <blockquote>
                      <p className={`text-[11px] md:text-xs font-medium leading-relaxed ${isCenter ? "text-white/90" : "text-slate-600"}`}>
                        “{item.quote}”
                      </p>
                    </blockquote>

                    <p className="mt-4 text-[10px] leading-relaxed opacity-70">
                      {item.seoKeywords}
                    </p>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>

          <button
            onClick={handleNext}
            className="absolute -right-4 md:right-0 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-xl hover:bg-[#0f766e]/10 hover:scale-110 transition-all text-[#0f766e] border border-slate-100"
            aria-label="Show next Sevaa Foundation testimonial"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;